import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { crmService } from '../services/crmService';
import { NewClient } from '../types/client';

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddClientModal: React.FC<AddClientModalProps> = ({ open, onClose, onSave }) => {
  const [newClient, setNewClient] = useState<NewClient>({ name: '', email: '', phone: '', address: '' });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewClient((prev: NewClient) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!newClient.name) {
      setError('Client name is required.');
      return;
    }
    
    const payload = {
      ...newClient,
      email: newClient.email || null,
    };

    setIsSaving(true);
    setError(null);
    try {
      await crmService.createClient(payload);
      onSave();
      onClose();
      setNewClient({ name: '', email: '', phone: '', address: '' }); // Reset form
    } catch (err: any) {
      console.error('Failed to create client:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData ? Object.values(errorData).join(' ') : 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Client</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField margin="normal" required fullWidth label="Client Name" name="name" value={newClient.name} onChange={handleChange} autoFocus />
          <TextField margin="normal" fullWidth label="Email Address" name="email" type="email" value={newClient.email || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Phone Number" name="phone" value={newClient.phone || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Address" name="address" multiline rows={3} value={newClient.address || ''} onChange={handleChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClientModal;